import {
  collection,
  doc,
  getCountFromServer,
  limit,
  query,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore'
import { useContext, useEffect, useRef, useState } from 'react'
import { firestore } from '../firebase'
import { GymMemberData, UserData } from '../types/types'
import { useUsersData } from './useUsersData'
import { Analytics } from '../services/analytics'
import { AppContext } from '../context/AppContext'
import useCustomFirestoreQueryData from './query/useCustomFirestoreQueryData'
import { useQuery } from '@tanstack/react-query'
import { useAuthContext } from '../context/AuthContext'
import { CollectionReference } from 'firebase/firestore'

type Props = {
  currentSpace: string | null
  onUpdateSuccess: () => void
  onUpdateFailure: () => void
  /**
   * Don't load removed members' records, names or photos until
   * loadRemovedMembers() is called. Their count is still available as
   * removedCount (a server-side count query)
   */
  loadRemovedLazily?: boolean
}

const usePeopleManagement = ({
  currentSpace,
  onUpdateSuccess,
  onUpdateFailure,
  loadRemovedLazily = false,
}: Props) => {
  const [updateInProgress, setUpdateInProgress] = useState(false)
  const [showRemovedMembers, setShowRemovedMembers] = useState(false)

  const aclCollection = collection(
    firestore,
    'spaces',
    currentSpace || 'no-id',
    'acl'
  ) as CollectionReference<GymMemberData, GymMemberData>
  const notSupport = where('role', '!=', 'support')
  // hasAccess + role filters need the (hasAccess, role) index on `acl`
  const blockedQuery = query(
    aclCollection,
    where('hasAccess', '==', false),
    notSupport
  )

  // With loadRemovedLazily, only members with access load up front. Blocked
  // members are counted on the server (1 read per 1,000 instead of 1 each)
  // and their records load when loadRemovedMembers() is called
  const {
    data: primaryAclData,
    refetch: refetchPrimaryAcl,
    isLoading: aclDataIsLoading,
  } = useCustomFirestoreQueryData<GymMemberData>({
    queryKey: ['acl', currentSpace, loadRemovedLazily ? 'with-access' : 'all'],
    query: loadRemovedLazily
      ? query(aclCollection, where('hasAccess', '==', true), notSupport)
      : query(aclCollection, notSupport),
    queryOptions: {
      enabled: !!currentSpace,
    },
  })

  const {
    data: blockedAclData,
    refetch: refetchBlockedAcl,
    isLoading: blockedAclIsLoading,
  } = useCustomFirestoreQueryData<GymMemberData>({
    queryKey: ['acl', currentSpace, 'blocked'],
    query: blockedQuery,
    queryOptions: {
      enabled: !!currentSpace && loadRemovedLazily && showRemovedMembers,
    },
  })

  const { isAuthed } = useAuthContext()
  const { data: blockedCount, refetch: refetchBlockedCount } = useQuery({
    queryKey: ['aclBlockedCount', currentSpace],
    queryFn: async () => (await getCountFromServer(blockedQuery)).data().count,
    enabled: !!currentSpace && loadRemovedLazily && isAuthed,
  })

  const refetchAclData = () => {
    refetchPrimaryAcl()
    if (!loadRemovedLazily) return
    refetchBlockedCount()
    if (showRemovedMembers) refetchBlockedAcl()
  }

  const aclData =
    loadRemovedLazily && primaryAclData
      ? [...primaryAclData, ...(blockedAclData || [])]
      : primaryAclData

  const userIDs =
    primaryAclData
      ?.filter((p: GymMemberData) => p.uid)
      .map((p: GymMemberData) => p.uid) || []

  const removedUserIDs =
    blockedAclData
      ?.filter((p: GymMemberData) => p.uid && !p.hasAccess)
      .map((p: GymMemberData) => p.uid) || []

  const removedCount = loadRemovedLazily
    ? blockedCount ?? 0
    : aclData?.filter((p: GymMemberData) => p.uid && !p.hasAccess).length || 0

  // getting user names and avatars. Removed members are a separate query so
  // loading them later doesn't refetch (and blank out) everyone else
  // keepPrevious: blocking or restoring someone changes these ID lists, which
  // would otherwise blank the page while every name reloads
  const { usersData: activeUsersData, usersDataIsLoading } = useUsersData({
    userIDs,
    keepPrevious: true,
  })
  const {
    usersData: removedUsersData,
    usersDataIsLoading: removedUsersDataIsLoading,
  } = useUsersData({ userIDs: removedUserIDs, keepPrevious: true })

  const usersData = activeUsersData
    ? { ...activeUsersData, ...(removedUsersData || {}) }
    : null

  const members =
    !!aclData && !!usersData
      ? aclData
          .map((p: GymMemberData) => {
            const dataExits =
              p !== undefined && usersData[p.uid] && usersData[p.uid].name

            return dataExits
              ? {
                  ...p,
                  name: usersData[p.uid].name,
                  photoURL: usersData[p.uid].photoURL,
                  joinDate:
                    typeof p.joinDate === 'number'
                      ? p.joinDate
                      : p.joinDate.seconds * 1000,
                }
              : undefined
          })
          .filter((i): i is NonNullable<typeof i> => i !== undefined)
          .sort((a, b) => a.name.localeCompare(b.name))
      : []

  const updateMemberData = async (newData: GymMemberData) => {
    if (!currentSpace) return
    const ref = doc(firestore, 'spaces', currentSpace, 'acl', newData.uid)

    setUpdateInProgress(true)

    const isStaff =
      newData.permissions?.canEditWorkouts ||
      newData.permissions?.canEditPermissions ||
      newData.permissions?.canRemoveComments ||
      newData.permissions?.canEditMembers ||
      newData.role === 'coach' ||
      newData.role === 'manager'

    try {
      await updateDoc(ref, {
        'permissions.canEditWorkouts': newData.permissions?.canEditWorkouts,
        'permissions.canEditMembers': newData.permissions?.canEditMembers,
        'permissions.canRemoveComments': newData.permissions?.canRemoveComments,
        'permissions.canEditPermissions':
          newData.permissions?.canEditPermissions,
        role: newData.role,
        isStaff: isStaff,
      })
      setUpdateInProgress(false)
      refetchAclData()
      Analytics._logEvent({
        name: 'gym_member_permissions_changed',
        params: { details: `member id: ${newData.uid}` },
      })
      onUpdateSuccess()
    } catch (err) {
      console.log('Error updating member data: ', err)
      setUpdateInProgress(false)
      onUpdateFailure()
    }
  }

  const restoreMember = async (newData: GymMemberData) => {
    if (!currentSpace) return
    const ref = doc(firestore, 'spaces', currentSpace, 'acl', newData.uid)

    setUpdateInProgress(true)

    const isStaff =
      newData.permissions?.canEditWorkouts ||
      newData.permissions?.canRemoveComments ||
      newData.permissions?.canEditPermissions ||
      newData.permissions?.canEditMembers ||
      newData.role === 'coach' ||
      newData.role === 'manager' ||
      newData.role === 'owner'

    try {
      await updateDoc(ref, {
        'permissions.canEditWorkouts': newData.permissions?.canEditWorkouts,
        'permissions.canRemoveComments':
          newData.permissions?.canRemoveComments || false,
        'permissions.canEditMembers': newData.permissions?.canEditMembers,
        'permissions.canEditPermissions':
          newData.permissions?.canEditPermissions,
        role: newData.role,
        hasAccess: true,
        isStaff: isStaff,
      })
      setUpdateInProgress(false)
      refetchAclData()
      Analytics._logEvent({
        name: 'gym_member_restored',
        params: { details: `member id: ${newData.uid}` },
      })
      onUpdateSuccess()
    } catch (err) {
      console.error('Error updating member data: ', err)
      setUpdateInProgress(false)
      onUpdateFailure()
    }
  }

  const changeMemberAccess = async (params: {
    data: GymMemberData
    hasAccess: boolean
  }) => {
    if (!currentSpace) return
    const ref = doc(firestore, 'spaces', currentSpace, 'acl', params.data.uid)
    setUpdateInProgress(true)
    try {
      await updateDoc(ref, {
        'permissions.canEditWorkouts': false,
        'permissions.canEditMembers': false,
        'permissions.canEditPermissions': false,
        'permissions.canRemoveComments': false,
        role: 'athlete',
        hasAccess: false,
        isStaff: false,
      })
      setUpdateInProgress(false)
      refetchAclData()
      Analytics._logEvent({
        name: 'gym_member_blocked',
        params: { details: `member id: ${params.data.uid}` },
      })
      onUpdateSuccess()
    } catch (err) {
      console.error('Error updating member data: ', err)
      setUpdateInProgress(false)
      onUpdateFailure()
    }
  }

  return {
    peopleAreLoading:
      !members.length && (aclDataIsLoading || usersDataIsLoading),
    members: members,
    updateInProgress: updateInProgress,
    showRemovedMembers: showRemovedMembers,
    // Once loaded, count the blocked members actually listed (members whose
    // account no longer exists have no name and aren't shown)
    removedCount:
      loadRemovedLazily && removedUsersData
        ? members.filter((m) => !m.hasAccess).length
        : removedCount,
    removedMembersAreLoading:
      loadRemovedLazily &&
      showRemovedMembers &&
      (blockedAclIsLoading || removedUsersDataIsLoading),
    updateMemberData: updateMemberData,
    changeMemberAccess: changeMemberAccess,
    loadRemovedMembers: () => setShowRemovedMembers(true),
    restoreMember: restoreMember,
  }
}
export default usePeopleManagement

// Hook
function usePrevious(value: any) {
  // The ref object is a generic container whose current property is mutable ...
  // ... and can hold any value, similar to an instance property on a class
  const ref = useRef()
  // Store current value in ref
  useEffect(() => {
    ref.current = value
  }, [value]) // Only re-run if value changes
  // Return previous value (happens before update in useEffect above)
  return ref.current
}
